import { Status } from '@df/core/status';
import { atomicColor } from '@df/ui/atomic/atomic-options';
import { NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { NICmsContentComponent } from '@icms/core/content';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import range from 'lodash-es/range';

export const defaultImage = '/cms/media/default.svg';
export const defaultImagePng = '/cms/media/default.png';
const mockedStatus = new Status();

ICmsContentService.registerComponentMetaPatch((meta: NICmsContentComponent.IMeta) => {
    // styles are only for non-block components
    if (!meta.block) {
        meta.fields.push(NICmsContentComponent.backgroundField);
    }
});

export const ICMS_CONFIG: NICmsClientConfig.IConfig = {
    modules: {
        campaigns: {
            enabled: true
        },
        markerIo: {
            enabled: true
        }
    },
    sizes: [
        {
            id: 'X',
            label: 'Large Desktop',
            width: 1920
        },
        {
            id: 'L',
            label: 'Desktop',
            width: 1440,
            default: true
        },
        {
            id: 'M',
            label: 'Tablet',
            width: 768,
            height: 1024
        },
        {
            id: 'S',
            label: 'Mobile',
            width: 375,
            height: 667
        }
    ],
    colors: atomicColor as NICmsClientConfig.IColorConfig[],
    spacings: range(0, 21).map(key => ({ key: `${key * 4}px`, title: `${key} (${key * 4}px)` })),
    resourcesMockup: {
        product: [
            {
                url: '/example-product',
                id: -1000,
                sku: '50044994',
                name: 'Example product',
                image: defaultImagePng,
                price: 16,
                verbosity: 3,
                breadcrumbs: [
                    {
                        label: 'Example category',
                        url: '/example-category'
                    },
                    {
                        label: 'Example subcategory',
                        url: '/example-category/example-subcategory'
                    }
                ],
                productId: -1000,
                media: [
                    {
                        image: defaultImagePng,
                        position: 1,
                        labels: ['base', 'small', 'thumbnail']
                    },
                    {
                        image: defaultImage,
                        position: 2
                    }
                ],
                meta: {
                    description:
                        'Example product description. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla vitae elit libero, a pharetra augue.',
                    title: 'Example product',
                    ogtype: 'product',
                    availability: 'in_stock'
                },
                oosVisible: 1,
                type: 'product'
            },
            {
                url: '/example-product',
                id: -1001,
                sku: '50044994',
                name: 'Second example of product',
                image: defaultImagePng,
                price: 100,
                orgPrice: 200,
                verbosity: 2,
                productId: -1001,
                media: [
                    {
                        image: defaultImagePng,
                        position: 1,
                        labels: ['base', 'small', 'thumbnail']
                    }
                ],
                meta: {
                    description:
                        'Example product description. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla vitae elit libero, a pharetra augue.',
                    title: 'Example product',
                    ogtype: 'product',
                    availability: 'in_stock'
                },
                oosVisible: 1,
                type: 'product'
            },
            {
                url: '/example-product',
                id: -1002,
                sku: '50044994',
                name: 'Third example of product',
                image: defaultImagePng,
                price: 200,
                verbosity: 2,
                productId: -1002,
                media: [
                    {
                        image: defaultImagePng,
                        position: 1,
                        labels: ['base', 'small', 'thumbnail']
                    }
                ],
                meta: {
                    description:
                        'Example product description. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla vitae elit libero, a pharetra augue.',
                    title: 'Example product',
                    ogtype: 'product',
                    availability: 'in_stock'
                },
                oosVisible: 1,
                type: 'product'
            }
        ],
        page: [
            {
                id: -100,
                type: 'page',
                store: 1,
                url: '/default-blog-category',
                name: 'Default blog category',
                typeData: {
                    tagRedirect: true,
                    topCopy: 'Latest news'
                },
                pageType: 'blog-root',
                position: 624,
                _cms: {
                    absoluteUrl: true,
                    status: 1,
                    url: '/default-blog-category'
                },
                verbosity: 4
            },
            {
                id: -101,
                type: 'page',
                store: 1,
                url: '/default-blog',
                breadcrumbs: [
                    {
                        label: 'Blog Category',
                        url: '/default-blog-category'
                    }
                ],
                name: 'Default blog post',
                parent: -100,
                image: defaultImage,
                typeData: {
                    readingTime: '3m',
                    ratio: 'ratio-3-1',
                    author: 'Jane',
                    date: '2022-08-21T22:00:00.000Z',
                    tags: [-100]
                },
                pageType: 'blog-post',
                position: 10,
                _cms: {
                    absoluteUrl: false,
                    status: 1,
                    slug: 'post-16',
                    url: '/default-blog-category/default-blog'
                },
                verbosity: 4
            }
        ]
    },
    specialPagesMockup: {
        checkoutSuccess: {
            number: '12345',
            customer: {
                id: 12345,
                firstname: 'John',
                lastname: 'Doe',
                email: 'john.doe@example.com',
                can_register: true
            },
            tracking: true,
            items: [
                {
                    id: 1,
                    product_id: -100,
                    name: 'Sample Product',
                    sku: 'SP-001',
                    qty_ordered: 2,
                    price: 50.0,
                    rowPrice: 100.0,
                    tax_amount: 10.0,
                    tax_percent: 10,
                    discount_amount: 5.0
                },
                {
                    id: 1,
                    product_id: -101,
                    name: 'Sample Product',
                    sku: 'SP-001',
                    qty_ordered: 4,
                    price: 75.0,
                    rowPrice: 100.0,
                    tax_amount: 10.0,
                    tax_percent: 10,
                    discount_amount: 5.0
                },
                {
                    id: 1,
                    product_id: -102,
                    name: 'Sample Product',
                    sku: 'SP-001',
                    qty_ordered: 6,
                    price: 128.4,
                    rowPrice: 100.0,
                    tax_amount: 10.0,
                    tax_percent: 10,
                    discount_amount: 5.0
                }
            ],
            total: 123.45,
            qty: 2,
            totals: {
                grand_total: 100,
                subtotal: 100
            },
            payment_method: {
                description: 'Credit Card',
                isCard: true,
                code: 'CC123',
                title: 'Credit Card',
                method: 'Visa',
                data: {
                    expiry_date: '12/23'
                },
                removeStatus: mockedStatus,
                setCardDefaultStatus: mockedStatus
            },
            delivery_date: '2022-08-21T22:00:00.000Z',
            shipping_method: {
                description: 'Shipping method'
            },
            shipping: {
                prefix: 'Mr.',
                firstname: 'John',
                lastname: 'Doe',
                company: 'Acme Corp',
                street_1: '123 Main St',
                street_2: 'Apt 4B',
                street_3: '',
                city: 'Metropolis',
                state: 'NY',
                country: 'USA',
                postcode: '12345',
                telephone: '555-1234'
            },
            billing: {
                prefix: 'Mr.',
                firstname: 'John',
                lastname: 'Doe',
                company: 'Acme Corp',
                street_1: '123 Main St',
                street_2: 'Apt 4B',
                street_3: '',
                city: 'Metropolis',
                state: 'NY',
                country: 'USA',
                postcode: '12345',
                telephone: '555-1234'
            }
        },
        search: {
            term: 'mockedSearch',
            data: {
                count: {
                    product: 2,
                    page: 0,
                    category: 1
                },
                terms: [],
                result: {
                    product: [-100, -101],
                    page: [],
                    category: [-100]
                },
                direct: {
                    meta: {
                        noOfResults: 10,
                        paginationStartFrom: 0,
                        totalResultsFound: 100,
                        notificationCode: 1,
                        qTime: 200,
                        offset: 0,
                        typeOfSearch: 'WILDCARD_AND',
                        debuggingInformation: 'query=example search term',
                        searchedTerm: 'example search term'
                    },
                    suggestionResults: [
                        {
                            id: 'terms',
                            suggestions: []
                        }
                    ],
                    queryResults: [
                        {
                            id: 'product',
                            meta: {
                                qTime: 2,
                                noOfResults: 10,
                                totalResultsFound: 2,
                                typeOfSearch: 'WILDCARD_AND',
                                offset: 0,
                                debuggingInformation: '',
                                searchedTerm: 'mockedSearch',
                                apiKey: 'klevu-171402899037617263'
                            },
                            records: [
                                {
                                    id: '-100'
                                },
                                {
                                    id: '-101'
                                }
                            ]
                        },
                        {
                            id: 'categories',
                            meta: {
                                qTime: 3,
                                noOfResults: 5,
                                totalResultsFound: 1,
                                typeOfSearch: 'WILDCARD_AND',
                                offset: 0,
                                debuggingInformation: '',
                                searchedTerm: 'mockedSearch',
                                apiKey: 'klevu-171402899037617263'
                            },
                            records: [
                                {
                                    id: '-100'
                                }
                            ]
                        },
                        {
                            id: 'cms',
                            meta: {
                                qTime: 18,
                                noOfResults: 5,
                                totalResultsFound: 0,
                                typeOfSearch: 'WILDCARD_AND',
                                offset: 0,
                                debuggingInformation: '',
                                searchedTerm: 'mockedSearch',
                                apiKey: 'klevu-171402899037617263'
                            },
                            records: []
                        }
                    ]
                }
            },
            options: {}
        }
    }
};
